const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'strike',
  'ul',
  'ol',
  'li',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
  'span'
]);

const REMOVE_WITH_CONTENT_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'meta',
  'link',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'template',
  'noscript'
]);

const ALLOWED_ANCHOR_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const CACHE_LIMIT = 1000;
const normalizationCache = new Map<string, string>();
const COLOR_STYLE_PROPERTIES = ['color', 'background-color'] as const;

/**
 * Normalizes rich-text HTML for status-page displays using a strict allow-list.
 */
export function normalizeStatusPageRichText(value: unknown): string {
  const source = normalizeSourceValue(value);
  if (!source) {
    return '';
  }

  const cached = normalizationCache.get(source);
  if (cached !== undefined) {
    return cached;
  }

  const normalized = sanitizeHtmlByAllowList(source);
  if (normalizationCache.size >= CACHE_LIMIT) {
    normalizationCache.clear();
  }
  normalizationCache.set(source, normalized);
  return normalized;
}

/**
 * Returns normalized rich text, or a fallback when normalized content is empty.
 */
export function normalizeStatusPageRichTextWithFallback(value: unknown, fallback: string): string {
  const normalized = normalizeStatusPageRichText(value);
  if (normalized.trim().length > 0) {
    return normalized;
  }
  return String(fallback ?? '');
}

function sanitizeHtmlByAllowList(source: string): string {
  const parser = new DOMParser();
  const documentRef = parser.parseFromString(source, 'text/html');
  const body = documentRef.body;
  if (!body) {
    return '';
  }

  sanitizeNodeChildren(body);
  return body.innerHTML.trim();
}

function normalizeSourceValue(value: unknown): string {
  if (typeof value === 'string') {
    return normalizeSpaceEntities(value).trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value).trim();
  }
  return '';
}

function sanitizeNodeChildren(parent: Element): void {
  const childNodes = Array.from(parent.childNodes);
  for (const node of childNodes) {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (REMOVE_WITH_CONTENT_TAGS.has(tagName)) {
      element.remove();
      continue;
    }

    sanitizeNodeChildren(element);

    if (!ALLOWED_TAGS.has(tagName)) {
      unwrapElement(element);
      continue;
    }

    applySafeInlineFormattingFromStyle(element, tagName);
    sanitizeElementAttributes(element, tagName);
  }
}

function sanitizeElementAttributes(element: HTMLElement, tagName: string): void {
  const safeStyle = getSafeInlineColorStyle(element);
  const attributes = Array.from(element.attributes);
  for (const attribute of attributes) {
    const name = attribute.name.toLowerCase();
    if (name === 'style' || name.startsWith('on')) {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (tagName !== 'a') {
      element.removeAttribute(attribute.name);
      continue;
    }

    if (!['href', 'target', 'rel'].includes(name)) {
      element.removeAttribute(attribute.name);
    }
  }

  if (safeStyle) {
    element.setAttribute('style', safeStyle);
  }

  if (tagName === 'a') {
    sanitizeAnchorAttributes(element as HTMLAnchorElement);
  }
}

function sanitizeAnchorAttributes(anchor: HTMLAnchorElement): void {
  const href = anchor.getAttribute('href');
  const safeHref = sanitizeAnchorHref(href);
  if (safeHref) {
    anchor.setAttribute('href', safeHref);
  } else {
    anchor.removeAttribute('href');
  }

  const target = (anchor.getAttribute('target') || '').toLowerCase();
  if (target !== '_blank') {
    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
    return;
  }

  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('rel', 'noopener noreferrer');
}

function sanitizeAnchorHref(href: string | null): string | null {
  const candidate = String(href || '').trim();
  if (!candidate) {
    return null;
  }

  if (candidate.startsWith('#') || candidate.startsWith('/')) {
    return candidate;
  }

  try {
    const parsed = new URL(candidate, 'https://status-page.local');
    if (ALLOWED_ANCHOR_PROTOCOLS.has(parsed.protocol.toLowerCase())) {
      return candidate;
    }
  } catch {
    return null;
  }

  return null;
}

function unwrapElement(element: HTMLElement): void {
  const parent = element.parentNode;
  if (!parent) {
    element.remove();
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  element.remove();
}

function normalizeSpaceEntities(value: string): string {
  return value
    .replaceAll(/\u00a0/g, ' ')
    .replaceAll(/&(nbsp|#160|#xa0);/gi, ' ');
}

function applySafeInlineFormattingFromStyle(element: HTMLElement, tagName: string): void {
  const styleText = String(element.getAttribute('style') || '');
  if (!styleText) {
    return;
  }

  const styleDeclaration = element.style;
  const wrappers: string[] = [];

  if (hasBoldStyle(styleDeclaration, styleText) && !['strong', 'b'].includes(tagName)) {
    wrappers.push('strong');
  }
  if (hasItalicStyle(styleDeclaration, styleText) && !['em', 'i'].includes(tagName)) {
    wrappers.push('em');
  }
  if (hasUnderlineStyle(styleDeclaration, styleText) && tagName !== 'u') {
    wrappers.push('u');
  }
  if (hasStrikethroughStyle(styleDeclaration, styleText) && !['s', 'del', 'strike'].includes(tagName)) {
    wrappers.push('s');
  }

  if (!wrappers.length) {
    return;
  }

  wrapNodeChildrenWithTags(element, wrappers);
}

function getSafeInlineColorStyle(element: HTMLElement): string {
  const styleText = String(element.getAttribute('style') || '');
  if (!styleText) {
    return '';
  }

  const safeDeclarations = COLOR_STYLE_PROPERTIES
    .map((propertyName) => {
      const rawValue = getDeclaredStyleValue(styleText, propertyName);
      const safeValue = normalizeSafeColorValue(rawValue);
      return safeValue ? `${propertyName}: ${safeValue};` : '';
    })
    .filter(Boolean);

  return safeDeclarations.join(' ');
}

function getDeclaredStyleValue(styleText: string, propertyName: string): string | null {
  const declarations = styleText.split(';');
  let matchedValue: string | null = null;

  for (const declaration of declarations) {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex === -1) {
      continue;
    }

    const name = declaration.slice(0, separatorIndex).trim().toLowerCase();
    if (name !== propertyName) {
      continue;
    }

    const value = declaration.slice(separatorIndex + 1).trim();
    if (value) {
      matchedValue = value;
    }
  }

  return matchedValue;
}

function normalizeSafeColorValue(value: string | null): string | null {
  const candidate = String(value || '').trim();
  if (!candidate) {
    return null;
  }

  const hexMatch = candidate.match(/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    return candidate.toLowerCase();
  }

  const rgbMatch = candidate.match(/^rgba?\(\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*,\s*([+-]?\d+)(?:\s*,\s*([+-]?(?:\d+|\d*\.\d+)))?\s*\)$/i);
  if (!rgbMatch) {
    return null;
  }

  const red = Number(rgbMatch[1]);
  const green = Number(rgbMatch[2]);
  const blue = Number(rgbMatch[3]);
  const alphaText = rgbMatch[4];

  if (![red, green, blue].every(isValidRgbChannel)) {
    return null;
  }

  if (alphaText === undefined) {
    return `rgb(${red}, ${green}, ${blue})`;
  }

  const alpha = Number(alphaText);
  if (!Number.isFinite(alpha) || alpha < 0 || alpha > 1) {
    return null;
  }

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function isValidRgbChannel(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 255;
}

function hasBoldStyle(styleDeclaration: CSSStyleDeclaration, styleText: string): boolean {
  const fontWeight = String(styleDeclaration.fontWeight || '').trim().toLowerCase();
  if (fontWeight === 'bold' || fontWeight === 'bolder') {
    return true;
  }
  const numericWeight = Number.parseInt(fontWeight, 10);
  if (!Number.isNaN(numericWeight) && numericWeight >= 600) {
    return true;
  }
  return /font-weight\s*:\s*(bold|bolder|[6-9]00)/i.test(styleText);
}

function hasItalicStyle(styleDeclaration: CSSStyleDeclaration, styleText: string): boolean {
  const fontStyle = String(styleDeclaration.fontStyle || '').trim().toLowerCase();
  if (fontStyle === 'italic' || fontStyle === 'oblique') {
    return true;
  }
  return /font-style\s*:\s*(italic|oblique)/i.test(styleText);
}

function hasUnderlineStyle(styleDeclaration: CSSStyleDeclaration, styleText: string): boolean {
  const textDecoration = `${styleDeclaration.textDecoration || ''} ${styleDeclaration.textDecorationLine || ''}`.toLowerCase();
  if (textDecoration.includes('underline')) {
    return true;
  }
  return /text-decoration(?:-line)?\s*:\s*[^;]*underline/i.test(styleText);
}

function hasStrikethroughStyle(styleDeclaration: CSSStyleDeclaration, styleText: string): boolean {
  const textDecoration = `${styleDeclaration.textDecoration || ''} ${styleDeclaration.textDecorationLine || ''}`.toLowerCase();
  if (textDecoration.includes('line-through')) {
    return true;
  }
  return /text-decoration(?:-line)?\s*:\s*[^;]*line-through/i.test(styleText);
}

function wrapNodeChildrenWithTags(element: HTMLElement, tags: string[]): void {
  const childNodes = Array.from(element.childNodes);
  if (!childNodes.length) {
    return;
  }

  const uniqueTags = Array.from(new Set(tags));
  const firstWrapper = element.ownerDocument.createElement(uniqueTags[0]);
  let currentWrapper = firstWrapper;

  for (let index = 1; index < uniqueTags.length; index += 1) {
    const nestedWrapper = element.ownerDocument.createElement(uniqueTags[index]);
    currentWrapper.appendChild(nestedWrapper);
    currentWrapper = nestedWrapper;
  }

  for (const childNode of childNodes) {
    currentWrapper.appendChild(childNode);
  }

  element.appendChild(firstWrapper);
}
